import { Fragment, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Users,
  Search,
  Clock,
  ChevronRight,
  MoreHorizontal,
  Flame,
  UserPlus,
  Loader2,
  Check,
  X,
  Plus,
  BookOpen,
  Link2,
  GraduationCap,
  Video,
  SlidersHorizontal,
} from 'lucide-react';
import { cn, Button, Paginator } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import {
  useTutorStudents,
  useTutorClasses,
  type TutorLinkedStudent,
  type ClassSummary,
} from '@/lib/tutor/queries';
import {
  useInviteStudent,
  useRespondStudentRequest,
  useUpdateTutorLinkSubjects,
} from '@/lib/tutor/mutations';
import {
  useAcademicMentees,
  useOffersAcademicMentoring,
  useSetOffersAcademicMentoring,
} from '@/lib/tutor/rendezvous';
import { useAlternantSwitch } from '@/lib/tutor/student-alternant';
import { splitStudentsAndAlternants, type AlternantEntry } from '@/lib/tutor/alternants';
import {
  useTutorInviteLinks,
  useCreateTutorInviteLink,
  useRevokeTutorInviteLink,
  buildInviteUrl,
  translateInviteError,
  type TutorInviteKind,
} from '@/lib/tutor-invite';
import { copyText } from '@/lib/clipboard';
import { InviteLinkField } from '@/components/invite-link-field';
import { useContactLayout, type TutorSectionId } from '@/lib/contact-layout';
import { MyConnectQr } from '@/components/contacts/my-connect-qr';
import {
  ContactLayoutButton,
  ContactLayoutPanel,
} from '@/components/contacts/contact-layout-settings';
import { filterByQuery, hasSearchQuery } from '@/lib/contact-search';
import { ContactSearchInput } from '@/components/contacts/contact-search-input';
import { FilesShortcut } from '@/components/documents/files-shortcut';
import { DiscussionsShortcut } from '@/features/discussions/discussions-shortcut';
import { StudentNameSearch } from '@/components/student-name-search';
import { AvatarCircle } from '@/components/avatar-circle';
import { SubjectsMultiselect } from '@/components/subjects-multiselect';
import { usePagedList } from '@/lib/use-paged-list';

const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

/** Textes dans lesquels la recherche cherche pour un élève (nom, niveau, matières du lien). */
function studentSearchFields(s: TutorLinkedStudent): string[] {
  return [
    s.display_name,
    s.level ? LEVEL_LABEL[s.level as SchoolLevel] : null,
    s.subjects.length === 0
      ? 'Toutes matières'
      : s.subjects.map((subject) => SUBJECT_LABEL[subject] ?? subject).join(' '),
  ].filter((f): f is string => !!f);
}

/** Textes dans lesquels la recherche cherche pour une classe (nom, matière, niveau, code). */
function classSearchFields(c: ClassSummary): Array<string | null> {
  return [
    c.name,
    c.subject ? (SUBJECT_LABEL[c.subject as SubjectSlug] ?? c.subject) : null,
    c.level ? (LEVEL_LABEL[c.level as SchoolLevel] ?? c.level) : null,
    c.invite_code,
  ];
}

function activity(s: TutorLinkedStudent): 'inactive' | 'active' {
  if (!s.last_activity_at) return 'inactive';
  const days = (Date.now() - new Date(s.last_activity_at).getTime()) / (1000 * 60 * 60 * 24);
  return days <= 7 ? 'active' : 'inactive';
}

export function TutorContactsPage() {
  const { user } = useAuth();
  const tutorId = user?.id ?? '';
  const { data: students = [], isLoading } = useTutorStudents(tutorId);
  const { data: classes = [], isLoading: classesLoading } = useTutorClasses(tutorId);
  const { data: mentees = [], isLoading: menteesLoading } = useAcademicMentees(tutorId);
  // Deux invitations distinctes : élève (matières, tutor_links) et étudiant
  // alternant (lien dédié, académique). Un seul panneau ouvert à la fois.
  const [invitePanel, setInvitePanel] = useState<'student' | 'academic' | null>(null);
  // Personnalisation des listes (ordre + listes affichées), enregistrée sur le compte.
  const layout = useContactLayout('tutor');
  const [customizeOpen, setCustomizeOpen] = useState(false);
  // Recherche dans les contacts : filtre local de toutes les listes affichées.
  const [query, setQuery] = useState('');
  const searching = hasSearchQuery(query);

  const activeClasses = classes.filter((c) => !c.archived_at);
  // Un étudiant alternant (rattaché OU relié mais « à rattacher ») change de groupe :
  // il quitte « Mes élèves » mais garde l'accès à sa fiche depuis sa carte alternant.
  const { pupils: active, alternants } = splitStudentsAndAlternants(students, mentees);
  const incomingRequests = students.filter(
    (s) => s.status === 'pending' && s.initiated_by === 'student',
  );
  const sentInvitations = students.filter(
    (s) => s.status === 'pending' && s.initiated_by === 'tutor',
  );

  // Listes filtrées par la recherche (sans recherche : identiques aux listes complètes).
  const shownClasses = filterByQuery(activeClasses, query, classSearchFields);
  const shownPupils = filterByQuery(active, query, studentSearchFields);
  const { items: pagePupils, paginator: pupilsPaginator } = usePagedList(shownPupils, {
    resetKeys: [query],
    unit: 'élèves',
    label: 'Pages des élèves',
  });
  const shownAlternants = filterByQuery(alternants, query, (a) => [a.displayName]);
  const shownIncoming = filterByQuery(incomingRequests, query, studentSearchFields);
  const shownSent = filterByQuery(sentInvitations, query, studentSearchFields);
  // Rien ne correspond parmi ce qui est affiché (listes activées + demandes) ?
  const visible = new Set(layout.visibleIds);
  const anyMatch =
    (visible.has('classes') && shownClasses.length > 0) ||
    (visible.has('students') && shownPupils.length > 0) ||
    (visible.has('alternants') && shownAlternants.length > 0) ||
    shownIncoming.length > 0 ||
    shownSent.length > 0;

  const sectionNodes: Record<TutorSectionId, ReactNode> = {
    // Pendant une recherche, une liste sans résultat disparaît (un « Aucune classe »
    // trompeur n'aurait aucun sens) ; sans recherche elle garde son état vide.
    classes:
      searching && shownClasses.length === 0 ? null : (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
              Mes classes ({shownClasses.length})
            </p>
            <Link
              to="/tutor/classes/new"
              className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1 text-xs font-semibold"
            >
              <Plus className="h-3.5 w-3.5" />
              Nouvelle classe
            </Link>
          </div>
          {classesLoading ? (
            <SkeletonList />
          ) : shownClasses.length === 0 ? (
            <EmptyClassesInline />
          ) : (
            <ul className="space-y-2">
              {shownClasses.map((cls) => (
                <ClassRow key={cls.id} cls={cls} />
              ))}
            </ul>
          )}
        </section>
      ),
    students:
      searching && shownPupils.length === 0 ? null : (
        <section className="space-y-3">
          <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
            Mes élèves ({shownPupils.length})
          </p>
          {isLoading ? (
            <SkeletonList />
          ) : shownPupils.length === 0 ? (
            <EmptyState
              label="Aucun élève lié"
              hint="Recherche un élève par nom ou par email pour l'inviter."
            />
          ) : (
            <>
              <ul className="space-y-2">
                {pagePupils.map((student) => (
                  <StudentCard key={student.link_id} student={student} tutorId={tutorId} />
                ))}
              </ul>
              <Paginator {...pupilsPaginator} />
            </>
          )}
        </section>
      ),
    // Mes étudiants alternants — mentorat académique, relation SÉPARÉE de
    // tutor_links (student_profiles.academic_tutor_id, migration 0237) — portent
    // leur propre bouton d'invitation, jamais mélangés avec les élèves classiques.
    // Le panneau d'invitation vit dans cette liste : on ne la masque pas tant qu'il est ouvert.
    alternants:
      searching && shownAlternants.length === 0 && invitePanel !== 'academic' ? null : (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
              Mes étudiants alternants ({shownAlternants.length})
            </p>
            <button
              type="button"
              onClick={() => setInvitePanel((v) => (v === 'academic' ? null : 'academic'))}
              className="text-primary-600 hover:text-primary-700 -my-2 inline-flex min-h-11 items-center gap-1 text-xs font-semibold"
            >
              <Plus className="h-3.5 w-3.5" />
              Inviter un étudiant
            </button>
          </div>

          {invitePanel === 'academic' && (
            <AcademicInvitePanel tutorId={tutorId} onClose={() => setInvitePanel(null)} />
          )}

          {menteesLoading ? (
            <SkeletonList />
          ) : shownAlternants.length === 0 ? (
            searching ? (
              <p className="text-ink-400 font-sans text-xs">Aucun résultat dans cette liste.</p>
            ) : (
              // Liste niche (mentorat académique) : état vide sur une ligne, pas une grande carte.
              <p className="text-ink-500 font-sans text-xs">
                <span className="text-ink-700 font-semibold">Aucun étudiant alternant</span> —
                invite-le par lien, ou passe un de tes élèves en alternant depuis sa carte (« … »).
              </p>
            )
          ) : (
            <ul className="space-y-2">
              {shownAlternants.map((entry) => (
                <MenteeCard key={entry.studentId} entry={entry} tutorId={tutorId} />
              ))}
            </ul>
          )}
        </section>
      ),
  };

  return (
    <div className="mx-auto max-w-lg space-y-6 px-4 py-6 pb-24 sm:py-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="text-primary-600 h-6 w-6" />
          <h1 className="text-ink-900 text-2xl font-bold">Mon réseau</h1>
        </div>
        <div className="flex items-center gap-2">
          <ContactLayoutButton open={customizeOpen} onClick={() => setCustomizeOpen((v) => !v)} />
          <Button
            size="sm"
            onClick={() => setInvitePanel((v) => (v === 'student' ? null : 'student'))}
            className="gap-1.5"
          >
            <UserPlus className="h-4 w-4" aria-hidden />
            <span aria-hidden className="sm:hidden">
              Inviter
            </span>
            <span className="sr-only sm:not-sr-only">Inviter un élève</span>
          </Button>
        </div>
      </div>

      <ContactSearchInput value={query} onChange={setQuery} />

      {customizeOpen && (
        <ContactLayoutPanel
          sections={layout.sections}
          isCustomized={layout.isCustomized}
          disabled={layout.isSaving}
          onToggle={layout.toggle}
          onMove={layout.move}
          onReset={layout.reset}
        />
      )}

      {/* Invite panel — élève */}
      {invitePanel === 'student' && (
        <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
          <p className="text-ink-900 text-sm font-semibold">Inviter un élève</p>
          <InvitePanel tutorId={tutorId} onClose={() => setInvitePanel(null)} />
        </div>
      )}

      {/* Demandes reçues — à traiter EN PREMIER (avant : tout en bas, sous les listes). */}
      {shownIncoming.length > 0 && (
        <section className="space-y-3">
          <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
            Demandes reçues ({shownIncoming.length})
          </p>
          <ul className="space-y-2">
            {shownIncoming.map((student) => (
              <IncomingRequestCard key={student.link_id} student={student} tutorId={tutorId} />
            ))}
          </ul>
        </section>
      )}

      {/* Listes de contacts — ordre et visibilité choisis par le tuteur (migration 0251).
          « Demandes reçues » et « Invitations envoyées » ci-dessous restent toujours affichées :
          ce sont des tâches à traiter, pas des listes. */}
      {layout.isLoading ? (
        <SkeletonList />
      ) : layout.visibleIds.length === 0 ? (
        <EmptyState
          icon={SlidersHorizontal}
          label="Aucune liste affichée"
          hint="Active au moins une liste avec le bouton « Personnaliser mes listes »."
        />
      ) : searching && !anyMatch ? (
        <EmptyState
          icon={Search}
          label={`Aucun résultat pour « ${query.trim()} »`}
          hint="Vérifie l'orthographe, ou efface la recherche pour revoir tous tes contacts."
        />
      ) : (
        layout.visibleIds.map((id) => (
          <Fragment key={id}>{sectionNodes[id as TutorSectionId]}</Fragment>
        ))
      )}

      {/* Invitations envoyées — en attente de l'élève */}
      {shownSent.length > 0 && (
        <section className="space-y-3">
          <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
            Invitations envoyées ({shownSent.length})
          </p>
          <ul className="space-y-2">
            {shownSent.map((student) => (
              <StudentCard key={student.link_id} student={student} tutorId={tutorId} />
            ))}
          </ul>
        </section>
      )}

      {/* Raccourcis (absents des barres mobiles), après les contacts — masqués pendant une recherche. */}
      {!hasSearchQuery(query) && <FilesShortcut to="/tutor/files" />}
      {!hasSearchQuery(query) && <DiscussionsShortcut to="/tutor/discussions" />}
    </div>
  );
}

function IncomingRequestCard({
  student,
  tutorId,
}: {
  student: TutorLinkedStudent;
  tutorId: string;
}) {
  const respond = useRespondStudentRequest();
  const isPending = respond.isPending;

  return (
    <li>
      <div className="border-primary-200 bg-primary-50 flex items-center gap-3 rounded-2xl border p-3">
        <AvatarCircle name={student.display_name} src={null} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="text-ink-900 truncate text-sm font-semibold">{student.display_name}</p>
          <p className="text-ink-500 mt-0.5 font-sans text-xs">
            {student.level
              ? `${LEVEL_LABEL[student.level as SchoolLevel] ?? student.level} — `
              : ''}
            souhaite te suivre
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() =>
              respond.mutate({
                tutorId,
                linkId: student.link_id,
                studentId: student.student_id,
                accept: false,
              })
            }
            disabled={isPending}
            className="text-ink-400 hover:text-error-500 hover:bg-error-50 flex h-11 w-11 items-center justify-center rounded-lg transition-colors"
            aria-label="Refuser"
            title="Refuser"
          >
            <X className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() =>
              respond.mutate({
                tutorId,
                linkId: student.link_id,
                studentId: student.student_id,
                accept: true,
              })
            }
            disabled={isPending}
            className="font-display bg-primary-500 hover:bg-primary-600 flex h-11 items-center gap-1.5 rounded-lg px-3 text-xs font-bold text-white transition-colors active:scale-[0.98]"
          >
            <Check className="h-3.5 w-3.5" />
            Accepter
          </button>
        </div>
      </div>
    </li>
  );
}

function MenteeCard({ entry, tutorId }: { entry: AlternantEntry; tutorId: string }) {
  const switcher = useAlternantSwitch(tutorId);
  const { studentId, displayName, isHigherEd, attached, linked } = entry;

  const identity = (
    <>
      <AvatarCircle name={displayName} src={null} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="text-ink-900 truncate text-sm font-semibold">{displayName}</p>
        {attached ? (
          <span className="bg-primary-50 text-primary-700 font-display mt-0.5 inline-flex h-5 items-center gap-1 rounded-full px-2 text-[10px] font-bold uppercase">
            <GraduationCap className="h-3 w-3" /> Entretiens académiques
          </span>
        ) : (
          <span className="bg-level-50 text-level-600 font-display mt-0.5 inline-flex h-5 items-center gap-1 rounded-full px-2 text-[10px] font-bold uppercase">
            <Clock className="h-3 w-3" /> À rattacher
          </span>
        )}
      </div>
    </>
  );

  return (
    <li className="border-ink-100 rounded-2xl border bg-white p-3">
      <div className="flex items-center gap-3">
        {linked ? (
          <Link
            to="/tutor/students/$studentId"
            params={{ studentId }}
            className="flex min-w-0 flex-1 items-center gap-3 transition-transform active:scale-[.98]"
          >
            {identity}
          </Link>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-3">{identity}</div>
        )}
        {attached ? (
          <Link
            to="/tutor/rendez-vous/new"
            search={{ kind: 'entretien', studentId }}
            className="border-ink-200 text-ink-700 hover:bg-ink-50 flex h-10 shrink-0 items-center gap-1.5 rounded-xl border px-3 text-xs font-semibold transition-colors active:scale-95"
          >
            <Video className="h-3.5 w-3.5" />
            Rendez-vous
          </Link>
        ) : (
          // Alternant relié mais qui n'a pas choisi ce tuteur : l'entretien exige d'être
          // son tuteur académique — le tuteur peut se rattacher directement (migration 0250).
          <button
            type="button"
            onClick={() => void switcher.toAlternant({ studentId, name: displayName })}
            disabled={switcher.isPending}
            className="bg-primary-500 hover:bg-primary-600 flex min-h-11 shrink-0 items-center gap-1.5 rounded-xl px-3 text-xs font-bold text-white transition-colors active:scale-95 disabled:opacity-50"
          >
            <GraduationCap className="h-3.5 w-3.5" />
            Me rattacher
          </button>
        )}
      </div>
      {/* Retour possible seulement si l'élève est rattaché ET reste relié à ce tuteur
          (tutor_links) : sinon rien à détacher, ou il disparaîtrait de toutes les listes. */}
      {attached && linked && (
        <button
          type="button"
          onClick={() =>
            void switcher.backToStudent({ studentId, name: displayName, declared: isHigherEd })
          }
          disabled={switcher.isPending}
          className="text-primary-600 mt-1 inline-flex min-h-11 items-center text-xs font-medium underline disabled:opacity-50"
        >
          {isHigherEd ? 'Retirer de mes étudiants alternants' : 'Repasser en élève'}
        </button>
      )}
      {switcher.error && (
        <p role="alert" className="text-error-500 text-xs">
          {switcher.error}
        </p>
      )}
    </li>
  );
}

function StudentCard({ student, tutorId }: { student: TutorLinkedStudent; tutorId: string }) {
  const isPending = student.status === 'pending';
  const isActive = !isPending && activity(student) === 'active';
  const [editingSubjects, setEditingSubjects] = useState(false);
  // Actions du lien (matières, alternance) repliées derrière « … » : avant, deux liens
  // soulignés sous CHAQUE carte doublaient la hauteur de la liste.
  const [manageOpen, setManageOpen] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState<SubjectSlug[]>(student.subjects);
  const updateSubjects = useUpdateTutorLinkSubjects([['tutor-students', student.student_id]]);
  const switcher = useAlternantSwitch(tutorId);

  const subjectsLabel =
    student.subjects.length === 0
      ? 'Toutes matières'
      : student.subjects.map((s) => SUBJECT_LABEL[s] ?? s).join(', ');

  const content = (
    <div className="border-ink-100 flex items-center gap-3 rounded-2xl border bg-white p-3 pr-14">
      <AvatarCircle name={student.display_name} src={null} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-ink-900 truncate text-sm font-semibold">{student.display_name}</p>
          {isPending ? (
            <span className="bg-level-50 text-level-600 font-display inline-flex h-5 items-center gap-1 rounded-full px-2 text-[10px] font-bold uppercase">
              <Clock className="h-3 w-3" /> En attente
            </span>
          ) : isActive ? (
            <span className="bg-xp-50 text-xp-600 font-display inline-flex h-5 rounded-full px-2 text-[10px] font-bold uppercase">
              Actif
            </span>
          ) : (
            <span className="bg-ink-100 text-ink-500 font-display inline-flex h-5 rounded-full px-2 text-[10px] font-bold uppercase">
              Inactif
            </span>
          )}
        </div>
        <div className="text-ink-500 mt-0.5 flex flex-wrap items-center gap-3 font-sans text-xs">
          {student.level && (
            <span>{LEVEL_LABEL[student.level as SchoolLevel] ?? student.level}</span>
          )}
          {!isPending && (
            <>
              <span className="font-mono">{student.xp} XP</span>
              <span className="flex items-center gap-0.5">
                <Flame className="text-accent-400 h-3 w-3" />
                {student.streak_days}j
              </span>
            </>
          )}
        </div>
        <p className="text-ink-400 mt-0.5 truncate font-sans text-xs">{subjectsLabel}</p>
      </div>
    </div>
  );

  // Bouton « … » posé SUR la carte mais hors du lien (pas de bouton dans un <a>).
  const manageButton = (
    <button
      type="button"
      aria-expanded={manageOpen}
      aria-label={`Gérer le lien avec ${student.display_name}`}
      onClick={() => setManageOpen((v) => !v)}
      className="text-ink-500 hover:bg-ink-100 absolute right-1.5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-xl"
    >
      <MoreHorizontal className="h-5 w-5" aria-hidden />
    </button>
  );

  const subjectsEditor = !manageOpen ? null : !editingSubjects ? (
    <div className="mt-1 flex flex-wrap items-center gap-x-4 px-1">
      <button
        type="button"
        onClick={() => {
          setSelectedSubjects(student.subjects);
          setEditingSubjects(true);
        }}
        className="text-primary-600 inline-flex min-h-11 items-center text-xs font-medium underline"
      >
        Modifier les matières
      </button>
      {/* Bascule dans la relation avec CE tuteur (migration 0250) : l'élève passe
          dans « Mes étudiants alternants » — jamais pour une invitation en attente. */}
      {!isPending && (
        <button
          type="button"
          onClick={() =>
            void switcher.toAlternant({ studentId: student.student_id, name: student.display_name })
          }
          disabled={switcher.isPending}
          className="text-primary-600 inline-flex min-h-11 items-center text-xs font-medium underline disabled:opacity-50"
        >
          Passer en étudiant alternant
        </button>
      )}
      {switcher.error && (
        <p role="alert" className="text-error-500 basis-full text-xs">
          {switcher.error}
        </p>
      )}
    </div>
  ) : (
    <div className="border-ink-100 mt-1 space-y-2 rounded-xl border bg-white p-2">
      <SubjectsMultiselect value={selectedSubjects} onChange={setSelectedSubjects} />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => setEditingSubjects(false)}>
          Annuler
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={updateSubjects.isPending}
          onClick={() =>
            updateSubjects.mutate(
              { linkId: student.link_id, subjects: selectedSubjects },
              { onSuccess: () => setEditingSubjects(false) },
            )
          }
        >
          {updateSubjects.isPending ? '…' : 'Confirmer'}
        </Button>
      </div>
    </div>
  );

  if (isPending) {
    return (
      <li>
        <div className="relative">
          {content}
          {manageButton}
        </div>
        {subjectsEditor}
      </li>
    );
  }
  return (
    <li>
      <div className="relative">
        <Link
          to="/tutor/students/$studentId"
          params={{ studentId: student.student_id }}
          className="block transition-transform active:scale-[.98]"
        >
          {content}
        </Link>
        {manageButton}
      </div>
      {subjectsEditor}
    </li>
  );
}

function ClassRow({ cls }: { cls: ClassSummary }) {
  return (
    <li>
      <Link
        to="/tutor/classes/$classId"
        params={{ classId: cls.id }}
        className="border-ink-100 hover:border-primary-300 flex items-center gap-3 rounded-2xl border bg-white p-3 transition-all active:scale-[.98]"
      >
        <div className="bg-primary-50 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
          <BookOpen className="text-primary-600 h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-ink-900 truncate text-sm font-semibold">{cls.name}</p>
            {cls.level && (
              <span className="bg-primary-100 text-primary-700 font-display inline-flex h-5 items-center rounded-full px-2 text-[10px] font-bold uppercase tracking-wide">
                {LEVEL_LABEL[cls.level as SchoolLevel] ?? cls.level}
              </span>
            )}
          </div>
          <div className="text-ink-500 mt-0.5 flex flex-wrap items-center gap-3 font-sans text-xs">
            {cls.subject && <span>{SUBJECT_LABEL[cls.subject as SubjectSlug] ?? cls.subject}</span>}
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {cls.member_count} élève{cls.member_count !== 1 ? 's' : ''}
            </span>
            <span className="text-ink-400 font-mono">{cls.invite_code}</span>
          </div>
        </div>
        <ChevronRight className="text-ink-400 h-4 w-4 shrink-0" />
      </Link>
    </li>
  );
}

function EmptyClassesInline() {
  return (
    <div className="border-ink-100 rounded-2xl border bg-white py-6 text-center">
      <BookOpen className="text-ink-300 mx-auto mb-2 h-8 w-8" />
      <p className="text-ink-500 text-sm font-medium">Aucune classe</p>
      <p className="text-ink-400 mx-auto mt-1 max-w-[260px] text-xs">
        Crée une classe et partage son code d'invitation à tes élèves.
      </p>
      <Link
        to="/tutor/classes/new"
        className="bg-primary-500 font-display hover:bg-primary-600 mt-4 inline-flex h-9 items-center gap-1.5 rounded-xl px-4 text-xs font-bold text-white shadow-md transition-all active:scale-[.98]"
      >
        <Plus className="h-3.5 w-3.5" />
        Nouvelle classe
      </Link>
    </div>
  );
}

function InvitePanel({ tutorId, onClose }: { tutorId: string; onClose: () => void }) {
  const [tab, setTab] = useState<'name' | 'email' | 'link' | 'qr'>('name');
  const [email, setEmail] = useState('');
  const [subjects, setSubjects] = useState<SubjectSlug[]>([]);
  const invite = useInviteStudent();

  async function handleEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    await invite.mutateAsync({ tutorId, email: email.trim(), subjects });
    setEmail('');
    onClose();
  }

  return (
    <div className="space-y-3">
      <div>
        <span className="text-ink-600 mb-1.5 block text-xs font-semibold">
          Pour quelle(s) matière(s) ? (optionnel — vide = toutes matières)
        </span>
        <SubjectsMultiselect value={subjects} onChange={setSubjects} />
      </div>

      <div className="bg-ink-100 flex gap-1 rounded-xl p-1">
        {(
          [
            ['name', 'Par nom'],
            ['email', 'Par email'],
            ['link', 'Lien'],
            ['qr', 'QR code'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              'font-display min-h-[2.75rem] flex-1 rounded-lg text-xs font-bold transition-colors',
              tab === key ? 'text-ink-900 bg-white shadow-sm' : 'text-ink-500 hover:text-ink-700',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'name' && (
        <StudentNameSearch tutorId={tutorId} subjects={subjects} onInvited={onClose} />
      )}

      {tab === 'email' && (
        <form onSubmit={handleEmail} className="space-y-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="eleve@exemple.fr"
            className="border-ink-200 bg-ink-50 text-ink-900 placeholder:text-ink-400 focus:border-primary-500 h-11 w-full rounded-xl border px-3.5 font-sans text-sm transition-colors focus:bg-white focus:outline-none"
            required
          />
          {invite.error && (
            <p className="text-error-500 bg-error-50 rounded-xl px-3 py-2 font-sans text-xs">
              {invite.error.message}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="border-ink-200 text-ink-700 font-display hover:border-ink-300 h-10 flex-1 rounded-xl border text-xs font-bold transition-colors"
            >
              Annuler
            </button>
            <Button type="submit" loading={invite.isPending} className="flex-1">
              {invite.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <UserPlus className="h-4 w-4" />
                  Inviter
                </>
              )}
            </Button>
          </div>
        </form>
      )}

      {tab === 'link' && <InviteLinkTab tutorId={tutorId} kind="student" subjects={subjects} />}

      {/* En face à face : l'élève scanne ton QR code, le lien est créé tout de suite
          (toutes matières — modifiables ensuite sur sa carte). */}
      {tab === 'qr' && <MyConnectQr userId={tutorId} />}
    </div>
  );
}

/**
 * Invitation d'un ÉTUDIANT ALTERNANT : uniquement par lien dédié (pas de
 * recherche par nom/email, qui créeraient un lien « élève » tutor_links). La
 * personne qui ouvre le lien s'inscrit directement comme étudiant·e en
 * alternance, sans aucun choix de statut, et est rattachée à ce tuteur comme
 * tuteur académique (cf. onboarding `?invite=` + redeem_tutor_invite_link).
 */
function AcademicInvitePanel({ tutorId, onClose }: { tutorId: string; onClose: () => void }) {
  return (
    <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-ink-900 text-sm font-semibold">Inviter un étudiant alternant</p>
          <p className="text-ink-500 mt-0.5 font-sans text-xs">
            Il crée son compte avec ce lien : il est automatiquement étudiant en alternance (aucun
            choix possible) et rattaché à toi pour ses entretiens académiques.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="text-ink-400 hover:text-ink-700 -mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <InviteLinkTab tutorId={tutorId} kind="academic" subjects={[]} />
    </div>
  );
}

/**
 * Lien d'invitation réutilisable (migration 0241) : la personne qui l'ouvre
 * s'inscrit (ou se connecte) et est automatiquement liée à CE tuteur — élève
 * classique (matières) ou étudiant en alternance (mentorat académique, pas de
 * matière). Le mode est FIXÉ par le panneau appelant (deux entrées distinctes,
 * plus de bascule). Un seul lien actif par mode, régénérer réutilise/actualise
 * le lien existant (cf. create_tutor_invite_link).
 */
function InviteLinkTab({
  tutorId,
  kind,
  subjects,
}: {
  tutorId: string;
  kind: TutorInviteKind;
  subjects: SubjectSlug[];
}) {
  const { data: offersMentoring, isLoading: mentoringLoading } =
    useOffersAcademicMentoring(tutorId);
  const setOffersMentoring = useSetOffersAcademicMentoring(tutorId);
  const { data: links = [] } = useTutorInviteLinks(tutorId);
  const create = useCreateTutorInviteLink();
  const revoke = useRevokeTutorInviteLink(tutorId);
  const [autoCopied, setAutoCopied] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const activeLink = links.find((l) => l.kind === kind);
  const academicDisabled = kind === 'academic' && !mentoringLoading && !offersMentoring;

  async function handleGenerate() {
    setAutoCopied(false);
    setGenerateError(null);
    try {
      const res = await create.mutateAsync({ tutorId, kind, subjects });
      // Copie « bonus » : après l'appel réseau le geste du clic est souvent perdu
      // (Safari) et elle peut être refusée — le lien reste de toute façon affiché
      // ci-dessous avec son bouton « Copier ».
      setAutoCopied(await copyText(buildInviteUrl(window.location.origin, res.token)));
    } catch (err) {
      // Affichée sous le bouton — jamais avalée en silence (la fonction SQL échouait
      // à chaque appel sans que rien ne s'affiche, cf. migration 0254).
      setGenerateError(translateInviteError(err instanceof Error ? err.message : ''));
    }
  }

  return (
    <div className="space-y-3">
      {academicDisabled ? (
        <div className="border-ink-100 rounded-2xl border bg-white p-5 text-center">
          <GraduationCap className="text-ink-300 mx-auto h-8 w-8" />
          <p className="text-ink-700 mt-2 font-sans text-sm font-semibold">
            Mentorat académique désactivé
          </p>
          <p className="text-ink-500 mt-1 font-sans text-xs">
            Active-le pour générer un lien d'invitation destiné à tes étudiants en alternance.
          </p>
          <Button
            className="mt-4 w-full"
            onClick={() => setOffersMentoring.mutate(true)}
            disabled={setOffersMentoring.isPending}
          >
            Activer le mentorat académique
          </Button>
        </div>
      ) : (
        <>
          {activeLink && (
            <div className="border-ink-100 rounded-2xl border bg-white p-3">
              <p className="text-ink-700 font-sans text-xs font-semibold">
                {kind === 'academic'
                  ? 'Lien actif — entretiens académiques'
                  : activeLink.subjects.length > 0
                    ? `Lien actif — ${activeLink.subjects.length} matière${activeLink.subjects.length > 1 ? 's' : ''}`
                    : 'Lien actif — toutes matières'}
              </p>
              <p className="text-ink-400 mt-0.5 font-sans text-[11px]">
                Utilisé {activeLink.useCount} fois
              </p>
              <div className="mt-2 space-y-2">
                <InviteLinkField url={buildInviteUrl(window.location.origin, activeLink.token)} />
                {autoCopied && (
                  <p className="font-sans text-xs font-medium text-emerald-600">
                    Lien copié dans le presse-papier.
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-accent-600 min-h-11 w-full"
                  onClick={() => revoke.mutate(activeLink.id)}
                  disabled={revoke.isPending}
                >
                  <X className="h-3.5 w-3.5" />
                  Révoquer ce lien
                </Button>
              </div>
            </div>
          )}

          <Button
            type="button"
            className="w-full"
            onClick={handleGenerate}
            loading={create.isPending}
          >
            <Link2 className="h-4 w-4" />
            {activeLink ? 'Régénérer avec ces réglages' : 'Générer le lien'}
          </Button>
          {generateError && (
            <p
              role="alert"
              className="text-error-500 bg-error-50 rounded-xl px-3 py-2 font-sans text-xs"
            >
              {generateError}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-2">
      {[1, 2].map((i) => (
        <div key={i} className="border-ink-100 animate-pulse rounded-2xl border bg-white p-3">
          <div className="flex items-center gap-3">
            <div className="bg-ink-100 h-10 w-10 rounded-full" />
            <div className="bg-ink-100 h-4 w-32 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({
  label,
  hint,
  icon: Icon = Users,
}: {
  label: string;
  hint: string;
  icon?: typeof Users;
}) {
  return (
    <div className="border-ink-100 rounded-2xl border bg-white py-6 text-center">
      <Icon className="text-ink-300 mx-auto mb-2 h-8 w-8" />
      <p className="text-ink-500 text-sm font-medium">{label}</p>
      <p className="text-ink-400 mx-auto mt-1 max-w-[260px] text-xs">{hint}</p>
    </div>
  );
}
